import type { ApiColumn, PaginationMeta } from "@/types/api";
import {
  type ScreenerLocation,
  type ScreenerLocationStatusType,
  ScreenerLocationStatusTypeWithUnpublished,
} from "@/types/screener-location";
import { UserListUser } from "@/types/user";
import type { Nullable } from "@/types/utils";

export enum ScreenerStatus {
  Deleted = "deleted",
  Draft = "draft",
  Preview = "preview",
  Published = "published",
  Unpublished = "unpublished",
}

// The derived status is determined on the BE taking into account both the
// screener status and its location statuses. Only screeners with a version
// number (referencing the version of that screener instance, NOT ScreenerType)
// of 1 can be in draft status
export enum DerivedScreenerStatus {
  Draft = "draft",
  Paused = "paused",
  Live = "live",
  Mixed = "mixed",
}

// When adding support for new Screener Versions, confirm that the
// `isLegacyScreenerType` util in utils/screener-builder is up to date.
export enum ScreenerType {
  // Initial CSV types
  v1 = "1.0",
  v2 = "2.0",
  // "m" indicates a multilingual screener
  v2m = "2.0m",
  // Initial AI builder type
  v2ai = "2.0ai",
  // 2.0aim combines AI Builder with multilingual support
  v2aim = "2.0aim",
  // 2.0.6 enabled custom scoring for CSV screeners
  v206 = "2.0.6",
  v206m = "2.0.6m",
  // 3.0 uses LangGraph orchestrator for question-by-question AI interviews
  v30 = "3.0",
  v30m = "3.0m",
}

export enum ScreenerMode {
  SinglePrompt = "single-prompt",
  TurnByTurn = "turn-by-turn",
}

export enum CompetencyFit {
  VeryHigh = "Very high",
  High = "High",
  Medium = "Medium",
  Low = "Low",
  VeryLow = "Very low",
  Absent = "Absent",
  NotAsked = "Not Asked",
}

export enum ClientFitProcessingStatus {
  New = "new",
  InProgress = "in_progress",
  Complete = "complete",
  Error = "error",
  Stale = "stale",
  Skipped = "skipped",
}

export enum ClientFitTierLabel {
  StrongMatch = "Strong Match",
  PotentialMatch = "Potential Match",
  Review = "Review",
}

export enum ClientFitNonNegotiableStatus {
  Met = "met",
  NotMet = "not_met",
  Unknown = "unknown",
}

export interface ClientFitSummary {
  completedAt: Nullable<string>;
  errorDescription?: Nullable<string>;
  evaluationId?: Nullable<string>;
  greenFlagCount: Nullable<number>;
  inputFingerprint?: Nullable<string>;
  modelName?: Nullable<string>;
  nonNegotiableStatus: Nullable<ClientFitNonNegotiableStatus>;
  profile?: {
    profileId: string;
    profileName: Nullable<string>;
    profileScope: "screener" | "screener_location";
    profileVersionId: string;
    profileVersionNumber: number;
  };
  processingStatus: Nullable<ClientFitProcessingStatus>;
  promptName?: Nullable<string>;
  promptVersion?: Nullable<string>;
  recommendationPdf?: {
    fileSize: Nullable<number>;
    generatedAt: Nullable<string>;
    generationError: Nullable<string>;
    pdfKey: Nullable<string>;
  };
  result?: Nullable<{
    confidence: Nullable<number>;
    greenFlags: ClientFitCriterionResult[];
    missingEvidence: ClientFitMissingEvidence[];
    nonNegotiables: ClientFitNonNegotiableResult[];
    rationale: string;
    yellowFlags: ClientFitCriterionResult[];
  }>;
  tierLabel: Nullable<ClientFitTierLabel>;
  tierRank: Nullable<number>;
  yellowFlagCount: Nullable<number>;
}

export interface ClientFitCriterionResult {
  criterionId: string;
  evidenceIds: string[];
  label: string;
  rationale: string;
}

export interface ClientFitNonNegotiableResult extends ClientFitCriterionResult {
  status: ClientFitNonNegotiableStatus;
}

export interface ClientFitMissingEvidence {
  criterionId: string;
  label: string;
  reason: string;
}

/**
 * Labels for candidate source types derived from referrer URL.
 * Used for display in tables, filters, and detail panels.
 */
export const CANDIDATE_SOURCE_LABELS: Record<string, string> = {
  redeployment: "Redeployment",
  reapplication: "Reapplication",
  general: "General",
};

export enum CompetencyResponseType {
  Written = "written",
  Voice = "voice",
  Skipped = "skipped",
}

export interface Competency {
  // Raw score is only available to internal users
  rawScore?: Nullable<number>;
  score?: CompetencyFit;
  quote: string;
  responseType?: CompetencyResponseType;
  audioPlaybackUrl?: string;
  transcriptionText?: string;
  /**
   * AS-2044: set when the recording can never be transcribed. Since AS-2043 the
   * 15-minute sweep skips these rows, so an absent transcript here is final
   * rather than pending.
   */
  transcriptionFailureReason?: string;
  /**
   * AS-2044: the recording this competency's result belongs to. Present so the assessment
   * transcript can join on identity rather than on position — see the pairing in
   * session-results.tsx.
   */
  audioS3Key?: string;
}

export const COMPETENCY_FIT_NUMERIC_MAP: Record<CompetencyFit, number> = {
  [CompetencyFit.VeryHigh]: 6,
  [CompetencyFit.High]: 5,
  [CompetencyFit.Medium]: 4,
  [CompetencyFit.Low]: 3,
  [CompetencyFit.VeryLow]: 2,
  [CompetencyFit.Absent]: 1,
  [CompetencyFit.NotAsked]: 0,
};

export interface Competencies {
  [name: string]: Competency;
}

export interface InterviewGuide {
  candidateHighlights: string[];
  potentialWeaknesses: string[];
  recommendedFollowUpQuestions: string[];
  generatedAt?: string;
  promptReference?: string;
}

export enum SessionStatus {
  Empty = "empty",
  Partial = "partial",
  Completed = "completed",
}

// "Root" question type to distinguish from subtypes such as Number or YesNo
// used within basic questions. From a screener builder Review page perspective
// Assessment questions are competencies
export enum RootQuestionTypes {
  Assessment = "assessment",
  Basic = "basic",
}

export enum MessageSenders {
  Candidate = "Candidate",
  Screener = "Screener",
}

export interface Question {
  createdAt: string;
  message: string;
  questionId: string;
  sender: MessageSenders;
  type: RootQuestionTypes;
}

export interface AssessmentQuestion extends Question {
  type: RootQuestionTypes.Assessment;
}

export interface BasicQuestion extends Question {
  answerStandardized?: string;
  internalLabel: string;
  inputType?: string;
  qualifyingPassed: Nullable<boolean>;
  questionOrder: number;
  questionRootId?: string;
  showInSummary: boolean;
  type: RootQuestionTypes.Basic;
}

export enum HighlightColor {
  Red = "red",
  Blue = "blue",
  Orange = "orange",
}

export const QUALIFYING_PASS_RATE_STRING_PATH =
  "qualifyingSummary.qualifyingPassRate";

// The by email response is formatted slightly differently from the by location response. We split them here for API
// responses and massage everything into `Screening` before passing it into the rest of the app.
interface BaseScreening {
  assessmentQuestions?: AssessmentQuestion[];
  basicQuestions?: BasicQuestion[];
  candidateEmail: string;
  candidateLink?: string;
  candidateName: Nullable<string>;
  candidatePhone: string;
  createdAt: string;
  highlightColor?: HighlightColor;
  jobTitle: string;
  language?: string;
  locationName: string;
  overallFit?: CompetencyFit;
  interviewGuide?: Nullable<InterviewGuide>;
  pdfLink?: string;
  pdfLocation?: string;
  positionLink?: string;
  // Qualifying summary is nullable for empty sessions, but should be available
  // in all other cases.
  qualifyingSummary: Nullable<{
    // Pass rate and questions passed are null when there are no qualifying
    // questions
    qualifyingPassRate: Nullable<number>;
    qualifyingQuestions: number;
    qualifyingQuestionsPassed: Nullable<number>;
  }>;
  // Raw scores are only available to internal users
  rawQualifyingQuestionsQualified?: Nullable<number>;
  rawQualifyingQuestionsTotal?: Nullable<number>;
  rawOverallFitScore?: Nullable<number>;
  screenerDisplayName?: string;
  screenerLocation?: ScreenerLocation;
  sessionId: string;
  sessionStatus: SessionStatus;
  time: string;
  totalTimeInSeconds: number;
  totalWords: number;
  viewedAt?: string;
  voiceQuestions: Question[];
  words: string;
  candidateSource?: "redeployment" | "reapplication" | "general" | null;
  clientFit?: ClientFitSummary;
  /**
   * True when the session has a backing `interview_session` row (3.0 flow:
   * native or migrated screener). TalentHub uses this to suppress the
   * redundant "Voice Questions" sidebar section for 3.0 — voice answers
   * are already inlined under the Assessment Questions transcript, so the
   * separate section would just duplicate them.
   */
  is3PointZero?: boolean;
}

export interface Screening extends BaseScreening {
  competencies?: {
    [competency: string]: Competency;
  };
  // These are added onto screenings fetched for the table, but will not be
  // available on screenings fetched by user (as there would be no others to
  // compare them with)
  highTimeSpent?: boolean;
  highWordCount?: boolean;
  screenerVersionNumber?: number;
  showInSummary?: {
    [key: string]: string | number;
  };
  showInSummaryMeta?: Record<
    string,
    { inputType?: string; questionRootId?: string }
  >;
}

export interface UserResponseScreening extends BaseScreening {
  competencyFields?: {
    [competency: string]: Competency;
  };
  showInSummaryFields?: {
    message: string | number;
    internalLabel: string;
  };
  showInSummaryMeta?: Record<
    string,
    { inputType?: string; questionRootId?: string }
  >;
}

export interface CandidateResponseScreener {
  company: string;
  companyId: string;
  id: string;
  internalName: string;
  jobTitle: string;
  type: ScreenerType;
}

export interface Screener {
  createdAt: string;
  hasUnpublishedChanges: boolean;
  id: string;
  internalName?: string;
  isDraft: boolean;
  jobTitle: string;
  lastSubmissionDate?: string;
  location: string[];
  screenings: number;
  status: ScreenerLocationStatusType;
  unviewedSessionCount: number;
  userId?: string;
  type: ScreenerType;
  version?: string;
  wage: string;
}

export interface ArchivedScreener extends Screener {
  archivedAt: string;
}

export type ScreenerOrArchivedScreener = Screener | ArchivedScreener;

// The by email response is formatted slightly differently from the by location response. We split them here for API
// responses and massage everything into `Screening` before passing it into the rest of the app.
interface BaseCandidateResponse {
  columns: ApiColumn<Screening>[];
  screener: CandidateResponseScreener;
  screenerLocation: { location: string };
  sessionId: string;
}

export interface CandidateResponse extends BaseCandidateResponse {
  screenerSessions: Screening[];
}

export interface UserCandidateResponse extends BaseCandidateResponse {
  screenerSessions: UserResponseScreening[];
}

export interface TableScreenings extends CandidateResponse {
  availableFilters: ResultsAvailableFilters;
  pagination: PaginationMeta;
}

export interface AllScreenersResponse<T extends ArchivedScreener | Screener> {
  availableFilters: {
    jobTitles: string[];
    locations: string[];
    statuses: ScreenerLocationStatusTypeWithUnpublished[];
    types: string[];
    users: UserListUser[];
  };
  columns: ApiColumn<T>[];
  pagination: PaginationMeta;
  screenerRows: T[];
}

export interface ResultsAvailableFilters {
  candidateSources?: string[];
  competencies: string[];
  highlightColors: string[];
  // Deploy-window union: AS-1845 flattened this to `string[]` but a
  // pre-deploy API may still emit `{ code, name }`. Narrow back to
  // `string[]` once rollout is complete. See `coerceLanguageCode`.
  languages: (string | { code: string })[];
  showInSummaryFields: string[];
}

export interface AllLocationsResponseScreener {
  company: string;
  companyId: string;
  hasUnpublishedChanges?: boolean;
  internalName: string;
  id: string;
  jobTitle: string;
  type: string;
}

export interface CsvUploadTranslationResponse {
  success: boolean;
  message: string;
}
